import React, { useState, useMemo } from 'react';
import { isDateAllowed } from '../utils/deliveryDates';

// allowedDates — белый список дат (ответ сервера о наличии): если передан, выбрать можно
// ТОЛЬКО эти даты, пустой массив = ни одной. null (по умолчанию) — без ограничения.
function Calendar({ selectedDate, onSelectDate, minDate, maxDate, onClose, disabledDates = [], availableDates = [], allowedDates = null }) {
  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
                       'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Вычисляем границы навигации и начальный месяц
  const { initialMonth, minMonth, maxMonth } = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonthIdx = now.getMonth();
    // Минимальный месяц — текущий
    const minM = { year: currentYear, month: currentMonthIdx };

    if (availableDates.length > 0) {
      // Первая доступная дата → начальный месяц
      const first = new Date(availableDates[0] + 'T00:00:00');
      const initial = { year: first.getFullYear(), month: first.getMonth() };
      // Последняя доступная дата → максимальный месяц
      const last = new Date(availableDates[availableDates.length - 1] + 'T00:00:00');
      const maxM = { year: last.getFullYear(), month: last.getMonth() };
      return { initialMonth: initial, minMonth: minM, maxMonth: maxM };
    }

    // Нет доступных дат — текущий месяц, можно перейти на +1
    const nextM = currentMonthIdx === 11
      ? { year: currentYear + 1, month: 0 }
      : { year: currentYear, month: currentMonthIdx + 1 };
    return { initialMonth: minM, minMonth: minM, maxMonth: nextM };
  }, [availableDates]);

  const [currentMonth, setCurrentMonth] = useState(
    new Date(initialMonth.year, initialMonth.month, 1)
  );

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Начало недели (понедельник = 0)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    // Пустые ячейки до первого дня
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Дни месяца
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Не позволяем выбрать дату раньше сегодняшнего дня (в любом случае)
    if (date < today) return true;
    const minDateObj = new Date(minDate);
    minDateObj.setHours(0, 0, 0, 0);
    if (date < minDateObj) return true;
    // Не позволяем выбрать дату позже максимальной доступной
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      maxDateObj.setHours(23, 59, 59, 999);
      if (date > maxDateObj) return true;
    }
    // Проверяем полностью заблокированные даты
    if (disabledDates.length > 0) {
      const dateStr = formatDateValue(date);
      if (disabledDates.includes(dateStr)) return true;
    }
    // Белый список наличия: вне него дата недоступна (в т.ч. за горизонтом каталога)
    if (!isDateAllowed(formatDateValue(date), allowedDates)) return true;
    return false;
  };

  const formatDateValue = (date) => {
    // Используем локальную дату, чтобы не было сдвига из-за UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const canGoPrev = currentMonth.getFullYear() > minMonth.year ||
    (currentMonth.getFullYear() === minMonth.year && currentMonth.getMonth() > minMonth.month);

  const canGoNext = currentMonth.getFullYear() < maxMonth.year ||
    (currentMonth.getFullYear() === maxMonth.year && currentMonth.getMonth() < maxMonth.month);

  const prevMonth = () => {
    if (!canGoPrev) return;
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    if (!canGoNext) return;
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (date) => {
    if (date && !isDateDisabled(date)) {
      onSelectDate(formatDateValue(date));
      if (onClose) onClose();
    }
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="calendar">
      <div className="calendar-header">
        <button
          type="button"
          className="calendar-nav"
          onClick={prevMonth}
          style={{ opacity: canGoPrev ? 1 : 0.3, cursor: canGoPrev ? 'pointer' : 'default' }}
          disabled={!canGoPrev}
        >‹</button>
        <span className="calendar-month">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button
          type="button"
          className="calendar-nav"
          onClick={nextMonth}
          style={{ opacity: canGoNext ? 1 : 0.3, cursor: canGoNext ? 'pointer' : 'default' }}
          disabled={!canGoNext}
        >›</button>
      </div>
      <div className="calendar-weekdays">
        {dayNames.map(day => (
          <div key={day} className="calendar-weekday">{day}</div>
        ))}
      </div>
      <div className="calendar-days">
        {days.map((date, index) => (
          <div
            key={index}
            className={`calendar-day ${!date ? 'empty' : ''} ${date && isDateDisabled(date) ? 'disabled' : ''} ${date && selectedDate === formatDateValue(date) ? 'selected' : ''}`}
            onClick={() => handleDateClick(date)}
          >
            {date ? date.getDate() : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Calendar;
